/**
 * 音乐封面服务
 * 处理前端默认封面和后端API封面的统一管理
 * 提供统一的封面获取接口，支持IndexedDB缓存、OneDrive文件ID转换等功能
 */

import axios from 'axios'
import tokenService from '@/utils/tokenService'
import { getRandomDefaultCover } from '@/utils/defaultCovers.js'
import { oneDriveAPI } from '@/api/onedrive'
import myRequest from '@/utils/myRequest'
import { createModuleLogger } from '@utils/logger'
import { resolveExternalCoverForDisplay } from '@/music/plugins/plazaCoverUtils.js'
const log = createModuleLogger('CoverService')

// 阿里云盘封面前缀（与后端 AliyunMediaController.ALIYUN_COVER_PREFIX 一致）
const ALIYUN_COVER_PREFIX = 'aliyun-cover:';

// 阿里云盘封面缓存使用的缩略图宽度：取高清档（1920），缓存一次到位，全屏展示也清晰。
// 缩略图走图片预览 CDN，不占用阿里云盘下载限流（非分段下载 3 次/60 秒）配额。
const ALIYUN_COVER_THUMBNAIL_WIDTH = 1920;

/**
 * 是否为阿里云盘封面标识（形如 "aliyun-cover:{fileId}"）
 */
function isAliyunCover(coverUrl) {
  return typeof coverUrl === 'string' && coverUrl.startsWith(ALIYUN_COVER_PREFIX);
}

/** 歌曲音频是否存储在阿里云盘（audio_storage_type = 2） */
function isAliyunStorageSong(song) {
  if (!song) return false;
  const storageType = song.audio_storage_type ?? song.audioStorageType ?? song.storageType;
  if (storageType === null || storageType === undefined || storageType === '') return false;
  const parsed = Number(storageType);
  return !Number.isNaN(parsed) && parsed === 2;
}

/**
 * 阿里云盘封面 fileId 与 OneDrive fileId 形态相近，需按歌曲存储类型归一化。
 */
function normalizeOriginalCoverUrl(song, coverUrl) {
  if (!coverUrl || typeof coverUrl !== 'string') return coverUrl;
  if (isAliyunCover(coverUrl)) return coverUrl;
  if (isAliyunStorageSong(song) && isOneDriveFileId(coverUrl)) {
    return ALIYUN_COVER_PREFIX + coverUrl;
  }
  return coverUrl;
}

/**
 * 向后端取阿里云盘封面图片 URL。
 * 后端优先返回「缩略图」直链（走图片预览 CDN，不吃下载限流），无缩略图时回退原图下载直链。
 * @param {string} fileId 阿里云盘封面 file_id
 * @param {boolean} refresh 仅对 source=download 的原图下载直链生效：跳过后端缓存重新申请全新直链
 * @param {number|null} width 缩略图宽度，默认取高清档 1920；source=download 时可传 null
 * @param {'thumbnail'|'download'} source thumbnail=缩略图 CDN；download=原图直链（无 x-oss-process）
 * @returns {Promise<string|null>} 图片 URL，失败返回 null
 */
async function fetchAliyunCoverDirectUrl(
  fileId,
  refresh = false,
  width = ALIYUN_COVER_THUMBNAIL_WIDTH,
  source = 'thumbnail'
) {
  try {
    const params = new URLSearchParams();
    if (refresh) params.set('refresh', 'true');
    if (source === 'download') {
      params.set('source', 'download');
    } else if (width) {
      params.set('width', String(width));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const resp = await myRequest.get(`/api/aliyun/media/cover-url/${encodeURIComponent(fileId)}${query}`);
    return resp?.url || null;
  } catch (e) {
    log.warn(`⚠️ [封面服务] 获取阿里云封面URL失败 (refresh=${refresh}, source=${source}):`, e);
    return null;
  }
}

/**
 * 直连阿里云盘 CDN 取封面字节。直链已被 OSS 拒绝时返回 { ok:false, status }。
 * 必须 no-referrer（CDN 有 Referer 白名单），且不附带任何凭证。
 */
async function fetchCoverBlobFromCdn(directUrl) {
  try {
    const imgResp = await fetch(directUrl, {
      method: 'GET',
      referrerPolicy: 'no-referrer',
      credentials: 'omit',
      cache: 'no-store'
    });
    if (!imgResp.ok) {
      return { ok: false, status: imgResp.status, blob: null };
    }
    return { ok: true, status: imgResp.status, blob: await imgResp.blob() };
  } catch (e) {
    log.warn('⚠️ [封面服务] 阿里云盘封面直连请求异常:', e);
    return { ok: false, status: 0, blob: null };
  }
}

/**
 * 解析阿里云盘封面：后端取图片 URL → 浏览器直连 CDN(no-referrer) 取字节 → blob → 写缓存。
 *
 * 后端优先返回「缩略图」直链：缩略图走图片预览 CDN，不属于「文件下载」，因此不受阿里云盘
 * 下载限流（非分段下载 3 次/60 秒 → 403）影响，是批量加载封面规避大面积 403 的正确方式。
 * 仅当文件无缩略图时后端才回退原图下载直链（受下载限流），此时首连失败再走一次强制刷新重试。
 * 全程图片字节都走浏览器↔CDN 直连，后端只传 URL，不占后端带宽。
 * @returns {Promise<string|null>} blob URL，失败返回 null
 */
async function loadAliyunCover(songId, coverUrlField) {
  try {
    const fileId = coverUrlField.slice(ALIYUN_COVER_PREFIX.length);
    let blob = null;

    // 缩略图（含 x-oss-process）对部分抖音封面/WebP 会返回 HTTP 400；
    // refresh=true 仍可能命中缩略图缓存，因此失败后需显式回退到 source=download 原图直链。
    const strategies = [
      { label: '缩略图1920', width: ALIYUN_COVER_THUMBNAIL_WIDTH, source: 'thumbnail', refresh: false },
      { label: '缩略图400', width: 400, source: 'thumbnail', refresh: false },
      { label: '原图直链', width: null, source: 'download', refresh: false },
      { label: '原图直链刷新', width: null, source: 'download', refresh: true }
    ];

    for (let i = 0; i < strategies.length; i++) {
      const strategy = strategies[i];
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 300 + Math.floor(Math.random() * 500)));
      }

      const directUrl = await fetchAliyunCoverDirectUrl(
        fileId,
        strategy.refresh,
        strategy.width,
        strategy.source
      );
      if (!directUrl) {
        continue;
      }

      const result = await fetchCoverBlobFromCdn(directUrl);
      if (result.ok) {
        blob = result.blob;
        if (i > 0) {
          log.debug(`✅ [封面服务] 阿里云盘封面回退成功: songId=${songId}, 策略=${strategy.label}`);
        }
        break;
      }

      log.warn(
        `⚠️ [封面服务] 阿里云盘封面直连失败: HTTP ${result.status}, 策略=${strategy.label}${i < strategies.length - 1 ? '，尝试下一策略' : ''}`
      );
    }

    if (!blob) {
      return null;
    }

    // 注意：阿里云盘存封面时不带图片 MIME，下载回来的 blob.type 往往是
    // application/octet-stream 或空，不能用 blob.type 判断是否图片，否则会误杀。
    // 封面必为图片，这里只校验大小，并统一重包成 image/jpeg 供 <img> 渲染。
    if (!blob || blob.size < 100) {
      return null;
    }
    const imageBlob = blob.type && blob.type.startsWith('image/')
      ? blob
      : new Blob([blob], { type: 'image/jpeg' });
    log.debug(`✅ [封面服务] 阿里云盘封面取图成功: songId=${songId}, 原始blob大小=${blob.size}, 原始type=${blob.type || '(空)'} → 重包为 ${imageBlob.type}`);
    // 写入 IndexedDB 缓存（用 aliyun-cover: 标识）
    try {
      await saveCoverToCache(String(songId), imageBlob, coverUrlField, false);
    } catch (e) {
      log.warn('⚠️ [封面服务] 阿里云盘封面缓存写入失败（不影响显示）:', e);
    }
    return URL.createObjectURL(imageBlob);
  } catch (e) {
    log.warn('⚠️ [封面服务] 阿里云盘封面加载异常:', e);
    return null;
  }
}

// 🎵 默认封面标识 - 使用 SVG 图标而不是 JPG 图片
const USE_SVG_ICON = 'USE_SVG_ICON';

function isDirectDisplayCoverUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed || trimmed === USE_SVG_ICON) return false;
  if (trimmed.includes('default_music_cover_') || trimmed.includes('default-music-cover-')) {
    return false;
  }
  return (
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/api/cover/proxy/') ||
    trimmed.startsWith('/api/music/')
  );
}

function resolveSongDisplayCoverUrl(song) {
  if (!song) return null;

  const candidates = [song.coverUrl, song.artwork, song.cover].filter(Boolean);
  for (const candidate of candidates) {
    if (isDirectDisplayCoverUrl(candidate)) {
      return candidate;
    }
  }

  const originalCover = song._originalCoverUrl || song.originalCoverUrl;
  if (originalCover && typeof originalCover === 'string') {
    const resolved = resolveExternalCoverForDisplay(originalCover, song);
    if (isDirectDisplayCoverUrl(resolved)) {
      return resolved;
    }
  }

  return null;
}

// 打印默认封面策略（调试用）
log.debug(`🎨 [封面服务] 使用 SVG 图标作为默认封面`);

// 封面缓存
const coverCache = new Map();

// 🎯 在线搜索结果封面缓存（专门用于搜索结果列表已加载的封面）
const onlineCoverCache = new Map();

// 🎯 封面加载状态锁（防止同一封面被多个流程同时加载）
const coverLoadingLocks = new Map(); // key: songId, value: Promise<string>

/**
 * 判断歌曲是否有后端封面
 * @param {Object} song - 歌曲对象
 * @returns {boolean}
 */
function hasBackendCover(song) {
  if (!song) return false;
  
  const coverUrl = song.coverUrl || song.cover;
  if (!coverUrl) return false;
  
  // 如果是 SVG 图标标识，说明没有后端封面
  if (coverUrl === USE_SVG_ICON) return false;
  
  // 如果是默认封面路径，说明没有后端封面
  const isDefaultCover = coverUrl.includes('/assets/default_music_cover_') ||
                         coverUrl.includes('default_music_cover_');
  
  // 检查是否为系统默认封面路径
  const isSystemDefaultCover = 
    coverUrl.includes('/static/music-covers/default-cover.jpg') ||
    coverUrl.includes('default-cover.jpg') ||
    coverUrl.includes('temp-cover') ||
    coverUrl.includes('default-music-cover-');
  
  return !isDefaultCover && !isSystemDefaultCover && (
    coverUrl.startsWith('/uploads/music/music-cover/') ||
    coverUrl.startsWith('/music/music-cover/') ||
    coverUrl.includes('/srv/uploads/music/music-cover/') ||
    coverUrl.includes('/srv/music-uploads/covers/') ||
    coverUrl.includes('music_') // 后端封面文件名通常包含music_
  );
}
/**
 * 🎯 缓存在线搜索结果的封面URL
 * @param {string|number} songId - 歌曲ID
 * @param {string} coverUrl - 封面URL
 */
export function cacheOnlineCover(songId, coverUrl) {
  if (!songId || !coverUrl) return;
  
  const id = String(songId);
  onlineCoverCache.set(id, coverUrl);
  log.debug(`🎨 [在线封面缓存] 缓存封面: ${id} -> ${coverUrl}`);
}

/**
 * 🎯 获取缓存的在线封面URL
 * @param {string|number} songId - 歌曲ID
 * @returns {string|null} 缓存的封面URL或null
 */
export function getCachedOnlineCover(songId) {
  if (!songId) return null;
  
  const id = String(songId);
  const cachedCover = onlineCoverCache.get(id);
  
  if (cachedCover) {
    log.debug(`🎨 [在线封面缓存] 命中缓存: ${id} -> ${cachedCover}`);
    return cachedCover;
  }
  
  return null;
}

/**
 * 🎯 智能获取歌曲封面URL（优先使用在线缓存）
 * @param {Object} song - 歌曲对象
 * @param {boolean} autoLoad - 是否自动加载封面，默认为false
 * @returns {string} 封面URL
 */
export function getSmartCoverUrl(song, autoLoad = false) {
  if (!song || !song.id) {
    log.warn('🖼️ [智能封面] 歌曲对象无效:', song);
    return USE_SVG_ICON;
  }

  const songId = String(song.id);

  const directCover = resolveSongDisplayCoverUrl(song);
  if (directCover) {
    if (!coverCache.has(songId) || coverCache.get(songId) !== directCover) {
      coverCache.set(songId, directCover);
    }
    return directCover;
  }
  
  // 🎯 第二步：优先检查在线搜索结果缓存
  const cachedOnlineCover = getCachedOnlineCover(songId);
  if (cachedOnlineCover && isDirectDisplayCoverUrl(cachedOnlineCover)) {
    return cachedOnlineCover;
  }
  
  // 🎯 第三步：检查常规缓存
  if (coverCache.has(songId)) {
    const cachedCover = coverCache.get(songId);
    if (isDirectDisplayCoverUrl(cachedCover)) {
      return cachedCover;
    }
  }

  // 🎯 第四步：检查是否正在加载中，避免重复加载
  if (coverCache.has(songId + '_loading')) {
    // 正在加载中，返回临时值，避免重复请求
    const tempUrl = coverCache.get(songId) || USE_SVG_ICON;
    return tempUrl;
  }

  // 🎯 第五步：尝试从 IndexedDB 缓存获取（异步查询，不阻塞返回）
  // 如果 IndexedDB 中有缓存，更新内存缓存和歌曲对象的 coverUrl
  const originalCoverUrl = song.coverUrl || song.cover;
  // 🔥 修复：使用原始 URL 而不是转换后的 API URL 来查询缓存
  const cacheIdentifier = originalCoverUrl && !originalCoverUrl.startsWith('blob:') && !originalCoverUrl.startsWith('http') 
    ? originalCoverUrl 
    : null;
  
  getCachedCover(songId, cacheIdentifier).then(cachedCover => {
    if (cachedCover && !cachedCover.isDefault && cachedCover.url) {
      // 更新内存缓存
      coverCache.set(songId, cachedCover.url);
      // 更新歌曲对象的 coverUrl（如果不同）
      if (song.coverUrl !== cachedCover.url) {
        song.coverUrl = cachedCover.url;
      }
      log.debug(`💾 [智能封面] 从 IndexedDB 缓存获取封面: ${songId}`);
    }
  }).catch(error => {
    // 静默处理错误，不影响同步返回
    console.debug(`[智能封面] IndexedDB 查询失败: ${songId}`, error);
  });

  // 🎯 第六步：使用原有逻辑获取封面（同步返回）
  return getSongCoverUrl(song, autoLoad);
}

/**
 * 获取歌曲封面URL（不自动加载）
 * @param {Object} song - 歌曲对象
 * @param {boolean} autoLoad - 是否自动加载封面，默认为false
 * @returns {string} 封面URL
 */
export function getSongCoverUrl(song, autoLoad = false) {
  if (!song || !song.id) {
    log.warn('🖼️ [封面服务] 歌曲对象无效:', song);
    return USE_SVG_ICON;
  }

  const songId = String(song.id);
  
  // 检查内存缓存。封面文件名变了就不用旧结果，避免换封面后仍显示上一张。
  if (coverCache.has(songId)) {
    const cachedCover = coverCache.get(songId);
    const source = [song._originalCoverUrl, song.coverUrl, song.cover].find(
      (url) => url && !String(url).startsWith('blob:') && !String(url).startsWith('data:')
    );
    const version = coverFileVersion(source);
    const cachedVersion = coverCache.get(`${songId}_ver`);
    if (!version || cachedVersion === version) {
      return cachedCover;
    }
    coverCache.delete(songId);
    coverCache.delete(`${songId}_ver`);
  }

  // 🎯 检查是否正在加载中，避免重复加载
  if (coverCache.has(songId + '_loading')) {
    // 正在加载中，返回临时值，避免重复请求
    const tempUrl = coverCache.get(songId) || USE_SVG_ICON;
    return tempUrl;
  }

  // 🎯 尝试从 IndexedDB 缓存获取（异步查询，不阻塞返回）
  // 如果 IndexedDB 中有缓存，更新内存缓存和歌曲对象的 coverUrl
  const originalCoverUrl = song.coverUrl || song.cover;
  // 🔥 修复：使用原始 URL 而不是转换后的 API URL 来查询缓存
  const cacheIdentifier = originalCoverUrl && !originalCoverUrl.startsWith('blob:') && !originalCoverUrl.startsWith('http') 
    ? originalCoverUrl 
    : null;
  
  getCachedCover(songId, cacheIdentifier).then(cachedCover => {
    if (cachedCover && !cachedCover.isDefault && cachedCover.url) {
      // 更新内存缓存
      coverCache.set(songId, cachedCover.url);
      // 更新歌曲对象的 coverUrl（如果不同）
      if (song.coverUrl !== cachedCover.url) {
        song.coverUrl = cachedCover.url;
      }
      log.debug(`💾 [封面服务] 从 IndexedDB 缓存获取封面: ${songId}`);
    }
  }).catch(error => {
    // 静默处理错误，不影响同步返回
    console.debug(`[封面服务] IndexedDB 查询失败: ${songId}`, error);
  });

  let coverUrl;

  // 1. 检查是否有有效的后端封面
  if (hasBackendCover(song)) {
    
    // 所有后端封面都使用API接口获取，并添加token认证
    const token = tokenService.getAccessToken();
    if (token) {
      // 🎯 通过URL参数传递token，让<img>标签可以直接加载
      const source = [song._originalCoverUrl, song.coverUrl, song.cover].find(
        (url) => url && !String(url).startsWith('blob:') && !String(url).startsWith('data:')
      );
      coverUrl = withCoverVersion(
        `/api/music/${song.id}/cover?token=${encodeURIComponent(token)}`,
        source
      );
      if (coverFileVersion(source)) {
        coverCache.set(`${songId}_ver`, coverFileVersion(source));
      }
      
      // 🔧 只有在明确要求自动加载时才异步加载封面
      if (autoLoad && !coverCache.has(songId + '_loading')) {
        coverCache.set(songId + '_loading', true); // 标记正在加载
        
        loadCoverWithCorrectHeaders(songId, token).then(blobUrl => {
          if (blobUrl) {
            coverCache.set(songId, blobUrl);
            coverCache.delete(songId + '_loading'); // 清除加载标记
            log.debug(`✅ [封面服务] 封面 ${songId} 已加载并缓存为blob URL`);
          }
        }).catch(error => {
          log.warn(`⚠️ [封面服务] 封面 ${songId} 加载失败:`, error);
          // 如果加载失败，使用默认封面
          coverCache.set(songId, USE_SVG_ICON);
          coverCache.delete(songId + '_loading'); // 清除加载标记
        });
      }
    } else {
      // 如果没有token，使用默认封面
      coverUrl = USE_SVG_ICON;
    }
  } else {
    // 2. 使用 SVG 图标作为默认封面
    log.debug(`🎨 [封面服务] 歌曲 ${songId} 没有后端封面，使用 SVG 图标`);
    coverUrl = USE_SVG_ICON;
  }

  // 缓存结果
  coverCache.set(songId, coverUrl);
  log.debug(`🎨 [封面服务] 歌曲 ${songId} 封面URL已缓存:`, coverUrl);
  return coverUrl;
}

/**
 * 使用正确的请求头加载封面
 * @param {string} songId - 歌曲ID
 * @param {string} token - 访问令牌
 * @returns {Promise<string|null>} blob URL或null
 */
async function loadCoverWithCorrectHeaders(songId, token) {
  try {
    const response = await fetch(`/api/music/${songId}/cover?token=${encodeURIComponent(token)}`, {
      cache: 'no-store',
      method: 'GET',
      headers: {
        'accept': 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
        'accept-language': 'zh-CN,zh;q=0.9,en;q=0.8,zh-TW;q=0.7',
        'cache-control': 'no-cache',
        'pragma': 'no-cache',
        'priority': 'i',
        'referer': window.location.origin + '/imusic',
        'sec-fetch-dest': 'image',
        'sec-fetch-mode': 'no-cors',
        'sec-fetch-site': 'same-origin',
        'user-agent': navigator.userAgent
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.startsWith('image/')) {
      throw new Error(`无效的图片类型: ${contentType || 'unknown'}`);
    }

    // 创建Blob URL
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    
    log.debug(`🎵 [封面服务] 封面 ${songId} 加载成功，创建blob URL`);
    return blobUrl;
    
  } catch (error) {
    log.error(`❌ [封面服务] 封面 ${songId} 加载失败:`, error);
    return null;
  }
}

/**
 * 预加载封面（用于优化用户体验）
 * @param {Array} songs - 歌曲列表
 */
export function preloadCovers(songs) {
  if (!Array.isArray(songs)) return;
  
  songs.forEach(song => {
    if (hasBackendCover(song)) {
      const token = tokenService.getAccessToken();
      if (token) {
        // 使用正确的请求头预加载封面
        loadCoverWithCorrectHeaders(song.id, token).then(blobUrl => {
          if (blobUrl) {
            coverCache.set(String(song.id), blobUrl);
            log.debug(`🖼️ [封面服务] 预加载成功: ${song.id}`);
          }
        }).catch(error => {
          log.warn(`🖼️ [封面服务] 预加载失败: ${song.id}，将使用默认封面`, error);
          // 如果预加载失败，更新缓存使用默认封面
          coverCache.set(String(song.id), USE_SVG_ICON);
        });
      }
    }
  });
}

/**
 * 清除封面缓存
 * @param {string|number} songId - 可选，指定歌曲ID，不传则清除所有缓存
 */
export function clearCoverCache(songId = null) {
  if (songId) {
    const id = String(songId);
    const cachedCover = coverCache.get(id);
    // 如果是blob URL，需要释放内存
    if (cachedCover && cachedCover.startsWith('blob:')) {
      URL.revokeObjectURL(cachedCover);
    }
    coverCache.delete(id);
    coverCache.delete(id + '_loading');
    coverCache.delete(id + '_ver');
    for (const key of coverQueryCache.keys()) {
      if (key === id || key.startsWith(`${id}_`)) {
        coverQueryCache.delete(key);
      }
    }
    log.debug(`🖼️ [封面服务] 清除歌曲 ${songId} 的封面缓存`);
  } else {
    // 清除所有缓存时，释放所有blob URL
    coverCache.forEach((url, id) => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    coverCache.clear();
    coverQueryCache.clear();
    log.debug('🖼️ [封面服务] 清除所有封面缓存');
  }
}

/**
 * 检查封面是否为默认封面
 * @param {string} coverUrl - 封面URL
 * @returns {boolean}
 */
export function isDefaultCover(coverUrl) {
  if (!coverUrl) return true;
  return coverUrl === USE_SVG_ICON || 
         coverUrl.includes('/assets/default_music_cover_') || 
         coverUrl.includes('default_music_cover_');
}

/**
 * 获取默认封面标识
 * @returns {string} 默认封面标识
 */
export function getDefaultCoverIdentifier() {
  return USE_SVG_ICON;
}

/**
 * 初始化封面服务 - 清除旧缓存
 */
function initializeCoverService() {
  log.debug('🖼️ [封面服务] 初始化，清除旧缓存');
  coverCache.clear();
}

// 在模块加载时初始化
initializeCoverService();

// ==================== IndexedDB 缓存相关 ====================

// IndexedDB 缓存配置
const COVER_CACHE_DB_NAME = 'MusicCoverCache';
const COVER_CACHE_DB_VERSION = 2;
const COVER_CACHE_STORE_NAME = 'covers';

// 全局数据库连接缓存
let coverCacheDB = null;
let isDBInitializing = false;

// 批量查询缓存优化 - 避免重复查询
const coverQueryCache = new Map();
const COVER_QUERY_CACHE_TTL = 5000; // 5秒缓存

// 存储配额检查缓存
let storageQuotaCache = null;
const STORAGE_QUOTA_CACHE_TTL = 30000; // 30秒缓存

/**
 * 检测移动端设备
 * @returns {boolean}
 */
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 检查存储配额（移动端兼容）
 * @returns {Promise<Object|null>}
 */
async function checkStorageQuota() {
  // 暂停存储配额检查：返回缓存的（若有）或直接跳过
  return storageQuotaCache ? storageQuotaCache.quota : null;
}

/**
 * 初始化IndexedDB封面缓存数据库
 * @returns {Promise<IDBDatabase>}
 */
function initCoverCacheDB() {
  return new Promise(async (resolve, reject) => {
    // 暂停：不再在初始化阶段检查存储配额
    
    // 如果已经有缓存的连接，直接返回
    if (coverCacheDB) {
      resolve(coverCacheDB);
      return;
    }
    
    // 如果正在初始化中，等待初始化完成
    if (isDBInitializing) {
      const checkDB = () => {
        if (coverCacheDB) {
          resolve(coverCacheDB);
        } else if (!isDBInitializing) {
          reject(new Error('数据库初始化失败'));
        } else {
          setTimeout(checkDB, 50);
        }
      };
      checkDB();
      return;
    }
    
    // 开始初始化
    isDBInitializing = true;
    const request = indexedDB.open(COVER_CACHE_DB_NAME, COVER_CACHE_DB_VERSION);
    
    request.onerror = () => {
      log.error('❌ [封面缓存] IndexedDB初始化失败:', request.error);
      isDBInitializing = false;
      reject(request.error);
    };
    
    request.onsuccess = () => {
      log.debug('✅ [封面缓存] IndexedDB初始化成功');
      coverCacheDB = request.result;
      isDBInitializing = false;
      
      // 移动端特殊处理：添加数据库连接监听
      if (isMobileDevice()) {
        coverCacheDB.onerror = (event) => {
          log.error('❌ [移动端缓存] 数据库连接错误:', event);
          // 移动端数据库连接错误时，尝试重新初始化
          setTimeout(() => {
            if (coverCacheDB) {
              coverCacheDB.close();
              coverCacheDB = null;
              isDBInitializing = false;
            }
          }, 1000);
        };
        
        coverCacheDB.onversionchange = () => {
          log.debug('🔄 [移动端缓存] 数据库版本变更，关闭连接');
          coverCacheDB.close();
          coverCacheDB = null;
          isDBInitializing = false;
        };
      }
      
      resolve(request.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // 创建封面存储对象仓库
      if (!db.objectStoreNames.contains(COVER_CACHE_STORE_NAME)) {
        const store = db.createObjectStore(COVER_CACHE_STORE_NAME, { keyPath: 'songId' });
        store.createIndex('url', 'url', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        log.debug('🔧 [封面缓存] 创建封面缓存存储仓库');
      }
    };
  });
}

/**
 * 从封面URL中提取文件名
 * @param {string} url - 封面URL
 * @returns {string|null}
 */
function coverFileVersion(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('blob:') || url.startsWith('data:')) return null;
  const name = extractCoverFileName(url);
  if (!name || name === 'cover') return null;
  return name;
}

function withCoverVersion(apiUrl, sourceUrl) {
  if (!apiUrl || apiUrl.includes('v=')) return apiUrl;
  const version = coverFileVersion(sourceUrl);
  if (!version) return apiUrl;
  const separator = apiUrl.includes('?') ? '&' : '?';
  return `${apiUrl}${separator}v=${encodeURIComponent(version)}`;
}

function extractCoverFileName(url) {
  if (!url) return null;
  
  try {
    // 处理不同的URL格式
    let fileName = null;
    
    // 检查是否是OneDrive文件ID
    if (isOneDriveFileId(url)) {
      fileName = url; // 直接使用文件ID作为文件名标识
      return fileName;
    }
    
    if (url.includes('/uploads/music/music-cover/')) {
      fileName = url.substring(url.lastIndexOf('/') + 1);
    } else if (url.includes('/music/music-cover/')) {
      fileName = url.substring(url.lastIndexOf('/') + 1);
    } else if (url.includes('/api/music/') && url.includes('/cover')) {
      // 🔥 修复：对于 API 接口，无法从 URL 中提取文件名
      // 应该返回 null，让调用方使用原始 URL 来提取文件名
      // 或者从原始 URL 中提取（如果提供了的话）
      return null;
    } else if (url.includes('/srv/uploads/music/music-cover/')) {
      // 🔥 新增：支持 /srv/uploads/ 路径
      fileName = url.substring(url.lastIndexOf('/') + 1);
    }
    
    return fileName;
  } catch (error) {
    log.warn(`⚠️ [文件名提取] 无法从URL提取文件名: ${url}`, error);
    return null;
  }
}

/**
 * 移动端缓存清理函数
 * @param {IDBObjectStore} store - IndexedDB对象存储
 * @param {number} count - 要清理的数量
 * @returns {Promise<void>}
 */
async function cleanupOldCovers(store, count = 10) {
  try {
    log.debug(`🧹 [移动端缓存] 开始清理 ${count} 个最旧的缓存`);
    
    // 获取所有缓存记录，按时间戳排序
    const getAllRequest = store.getAll();
    
    return new Promise((resolve, reject) => {
      getAllRequest.onsuccess = () => {
        const allCovers = getAllRequest.result;
        
        if (allCovers.length <= count) {
          log.debug(`🧹 [移动端缓存] 缓存数量 ${allCovers.length} 不超过清理阈值 ${count}，跳过清理`);
          resolve();
          return;
        }
        
        // 按时间戳排序，删除最旧的
        allCovers.sort((a, b) => a.timestamp - b.timestamp);
        const toDelete = allCovers.slice(0, count);
        
        log.debug(`🧹 [移动端缓存] 准备删除 ${toDelete.length} 个旧缓存`);
        
        // 逐个删除
        let deletedCount = 0;
        const deleteNext = (index) => {
          if (index >= toDelete.length) {
            log.debug(`✅ [移动端缓存] 清理完成，删除了 ${deletedCount} 个旧缓存`);
            resolve();
            return;
          }
          
          const deleteRequest = store.delete(toDelete[index].songId);
          deleteRequest.onsuccess = () => {
            deletedCount++;
            deleteNext(index + 1);
          };
          deleteRequest.onerror = () => {
            log.warn(`⚠️ [移动端缓存] 删除旧缓存失败: ${toDelete[index].songId}`, deleteRequest.error);
            deleteNext(index + 1);
          };
        };
        
        deleteNext(0);
      };
      
      getAllRequest.onerror = () => {
        log.error('❌ [移动端缓存] 获取缓存列表失败:', getAllRequest.error);
        reject(getAllRequest.error);
      };
    });
  } catch (error) {
    log.error('❌ [移动端缓存] 清理旧缓存异常:', error);
    throw error;
  }
}

/**
 * 从IndexedDB获取缓存的封面
 * @param {string|number} songId - 歌曲ID
 * @param {string} currentCoverUrl - 当前封面URL（用于文件名匹配）
 * @returns {Promise<Object|null>} 缓存的封面对象或null
 */
async function getCachedCover(songId, currentCoverUrl = null) {
  // 检查查询缓存，避免重复查询
  const cacheKey = `${songId}_${currentCoverUrl || ''}`;
  const cached = coverQueryCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < COVER_QUERY_CACHE_TTL) {
    return cached.result;
  }
  try {
    // 使用缓存的数据库连接，避免重复初始化
    const db = await initCoverCacheDB();
    
    // 检查数据库连接是否有效
    if (!db || !db.objectStoreNames) {
      log.warn('⚠️ [封面缓存] 数据库连接无效，跳过缓存查询');
      return null;
    }
    
    const transaction = db.transaction([COVER_CACHE_STORE_NAME], 'readonly');
    const store = transaction.objectStore(COVER_CACHE_STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.get(songId);
      
      request.onsuccess = () => {
        const result = request.result;
        
        if (result && result.blob) {
          // 检查缓存是否过期（90天，长期缓存）
          const now = Date.now();
          const cacheAge = now - result.timestamp;
          const maxAge = 90 * 24 * 60 * 60 * 1000; // 90天
          
          if (cacheAge < maxAge) {
            // 检查文件名是否匹配（多设备缓存同步 + 移动端兼容）
            const currentFileName = coverFileVersion(currentCoverUrl);
            const cachedFileName = result.coverFileName || coverFileVersion(result.url);
            // 没有记下文件名的旧缓存无法区分是不是这次封面，换过封面后必须丢掉
            if (currentFileName && (!cachedFileName || currentFileName !== cachedFileName)) {
                log.debug(`🔄 [封面缓存] 文件名不匹配，需要重新加载: 缓存=${result.coverFileName}, 当前=${currentFileName}`);
                
                // 移动端特殊处理：异步删除旧缓存，避免阻塞
                if (isMobileDevice()) {
                  const writeTransaction = db.transaction([COVER_CACHE_STORE_NAME], 'readwrite');
                  const writeStore = writeTransaction.objectStore(COVER_CACHE_STORE_NAME);
                  const deleteRequest = writeStore.delete(songId);
                  deleteRequest.onsuccess = () => {
                    log.debug(`🗑️ [移动端缓存] 旧缓存已删除: ${songId}`);
                  };
                  deleteRequest.onerror = (error) => {
                    log.warn(`⚠️ [移动端缓存] 删除旧缓存失败: ${songId}`, error);
                  };
                } else {
                  // PC端同步删除
                  const writeTransaction = db.transaction([COVER_CACHE_STORE_NAME], 'readwrite');
                  const writeStore = writeTransaction.objectStore(COVER_CACHE_STORE_NAME);
                  writeStore.delete(songId);
                }
                
                resolve(null);
                return;
              }
            
            // 创建Blob URL
            const blobUrl = URL.createObjectURL(result.blob);
            const resultData = {
              url: blobUrl,
              isDefault: result.isDefault || false,
              cached: true
            };
            // 缓存查询结果
            coverQueryCache.set(cacheKey, {
              result: resultData,
              timestamp: Date.now()
            });
            resolve(resultData);
          } else {
            // 缓存过期，删除旧缓存
            log.debug(`⏰ [封面缓存] 歌曲 ${songId} 的封面缓存已过期，删除旧缓存`);
            const writeTransaction = db.transaction([COVER_CACHE_STORE_NAME], 'readwrite');
            const writeStore = writeTransaction.objectStore(COVER_CACHE_STORE_NAME);
            writeStore.delete(songId);
            resolve(null);
          }
        } else {
          // 缓存空结果，避免重复查询
          coverQueryCache.set(cacheKey, {
            result: null,
            timestamp: Date.now()
          });
          resolve(null);
        }
      };
      
      request.onerror = () => {
        log.error('❌ [封面缓存] 获取缓存封面失败:', request.error);
        resolve(null);
      };
    });
  } catch (error) {
    log.error('❌ [封面缓存] 获取缓存封面异常:', error);
    return null;
  }
}

/**
 * 将封面保存到IndexedDB缓存
 * @param {string|number} songId - 歌曲ID
 * @param {Blob} blob - 封面Blob对象
 * @param {string} url - 封面URL（用于标识）
 * @param {boolean} isDefault - 是否为默认封面
 * @returns {Promise<boolean>} 是否保存成功
 */
async function saveCoverToCache(songId, blob, url, isDefault = false) {
  const isMobile = isMobileDevice();
  const maxRetries = isMobile ? 3 : 1; // 移动端增加重试次数
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // 使用缓存的数据库连接
      const db = await initCoverCacheDB();
      
      // 检查数据库连接是否有效
      if (!db || !db.objectStoreNames) {
        log.warn(`⚠️ [封面缓存] 数据库连接无效，跳过缓存保存 (尝试 ${attempt}/${maxRetries})`);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500 * attempt));
          continue;
        }
        return false;
      }
      
      const transaction = db.transaction([COVER_CACHE_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(COVER_CACHE_STORE_NAME);
      
      // 移动端特殊处理：检查存储空间
      if (isMobile && blob) {
        const quota = await checkStorageQuota();
        if (quota && quota.quota && quota.usage) {
          const availableSpace = quota.quota - quota.usage;
          const requiredSpace = blob.size * 2; // 预留2倍空间
          if (availableSpace < requiredSpace) {
            log.warn(`⚠️ [移动端缓存] 存储空间不足，需要 ${(requiredSpace/1024/1024).toFixed(2)}MB，可用 ${(availableSpace/1024/1024).toFixed(2)}MB`);
            // 移动端存储空间不足时，清理旧缓存
            await cleanupOldCovers(store, 10);
          }
        }
      }
      
      const cacheData = {
        songId,
        blob,
        url,
        isDefault,
        timestamp: Date.now(),
        // 存储封面文件名用于缓存失效检查
        coverFileName: extractCoverFileName(url)
      };
      
      return new Promise((resolve, reject) => {
        const request = store.put(cacheData);
        
        request.onsuccess = () => {
          log.debug(`💾 [封面缓存] 歌曲 ${songId} 封面已缓存 (尝试 ${attempt}/${maxRetries})`);
          resolve(true);
        };
        
        request.onerror = (error) => {
          log.error(`❌ [封面缓存] 保存封面缓存失败 (尝试 ${attempt}/${maxRetries}):`, error);
          
          // 移动端特殊处理：如果是存储配额错误，尝试清理缓存
          if (isMobile && error.name === 'QuotaExceededError') {
            log.debug('🧹 [移动端缓存] 存储配额超限，尝试清理旧缓存');
            cleanupOldCovers(store, 20).then(() => {
              resolve(false); // 清理后返回false，让外层重试
            }).catch(() => {
              resolve(false);
            });
          } else {
            resolve(false);
          }
        };
      });
    } catch (error) {
      log.error(`❌ [封面缓存] 保存封面缓存异常 (尝试 ${attempt}/${maxRetries}):`, error);
      
      // 如果不是最后一次尝试，等待一段时间再重试
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * attempt, 3000); // 递增延迟，最大3秒
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  log.error(`❌ [封面缓存] 保存封面缓存失败，已重试${maxRetries}次 ${songId}`);
  return false;
}

// ==================== OneDrive 相关 ====================

/**
 * 检查是否是OneDrive文件ID
 * @param {string} coverUrl - 封面URL
 * @returns {boolean}
 */
function isOneDriveFileId(coverUrl) {
  if (!coverUrl || typeof coverUrl !== 'string') {
    return false;
  }
  
  // 阿里云盘封面有专属前缀，不是 OneDrive 文件ID
  if (coverUrl.startsWith('aliyun-cover:')) {
    return false;
  }
  
  // 如果包含URL特征，说明不是OneDrive文件ID
  if (coverUrl.startsWith('http://') || 
      coverUrl.startsWith('https://') || 
      coverUrl.startsWith('/uploads/') ||
      coverUrl.startsWith('/') ||
      coverUrl.includes('://')) {
    return false;
  }
  
  // OneDrive文件ID通常是长字符串（至少20个字符）
  if (coverUrl.length < 15) {
    return false;
  }
  
  // 如果看起来像是文件路径（包含斜杠），不是OneDrive文件ID
  if (coverUrl.includes('/')) {
    return false;
  }
  
  // 排除明显的文件扩展名格式（如 .jpg, .png 等）
  if (coverUrl.includes('.') && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(coverUrl)) {
    return false;
  }
  
  // OneDrive文件ID通常包含字母和数字，可能包含 ! 字符
  // 检查是否主要由字母、数字、!、-、_ 组成
  const onedriveIdPattern = /^[A-Za-z0-9!\-_]+$/;
  if (!onedriveIdPattern.test(coverUrl)) {
    return false;
  }
  
  return true;
}

// ==================== 封面URL转换相关 ====================

/**
 * 将后端文件系统路径转换为API访问路径
 * @param {string} coverUrl - 封面URL
 * @param {string|number} songId - 歌曲ID
 * @returns {string|null}
 */
function convertToHttpCoverUrl(coverUrl, songId) {
  if (!coverUrl) return null;
  
  // 如果已经是HTTP路径，直接返回
  if (coverUrl.startsWith('http')) {
    return coverUrl;
  }
  
  // 如果已经是API路径，直接返回
  if (coverUrl.startsWith('/api/music/')) {
    return coverUrl;
  }
  
  // 如果是文件系统路径，转换为API路径
  if (coverUrl.includes('/srv/uploads/music/music-cover/') || 
      coverUrl.includes('/srv/music-uploads/covers/') ||
      coverUrl.startsWith('/uploads/music/music-cover/')) {
    
    if (!songId) {
      log.warn('🔄 [封面路径转换] 缺少songId，无法转换为API路径:', coverUrl);
      return null;
    }
    
    // 获取token并构造API路径
    const token = tokenService.getAccessToken();
    const apiUrl = withCoverVersion(`/api/music/${songId}/cover`, coverUrl);
    
    log.debug(`🔄 [封面路径转换] ${coverUrl} -> ${apiUrl}`);
    return apiUrl;
  }
  
  // 其他情况直接返回
  return coverUrl;
}

// ==================== 网络加载相关 ====================

/**
 * 从网络加载封面图片（带重试机制）
 * @param {string} coverUrl - 封面URL
 * @param {string|number} songId - 歌曲ID
 * @param {number} retryCount - 重试次数
 * @param {string} originalCoverUrl - 原始封面URL（用于文件名提取，可选）
 * @returns {Promise<Object|null>} 加载结果对象或null
 */
async function loadCoverFromNetwork(coverUrl, songId, retryCount = 3, originalCoverUrl = null) {
  let lastError = null;
  
  for (let attempt = 1; attempt <= retryCount; attempt++) {
    try {
      log.debug(`🌐 [封面缓存] 开始从网络加载封面 (尝试 ${attempt}/${retryCount}): ${songId} -> ${coverUrl}`);
      
      // 构造请求头 - 使用与curl请求相同的头
      const headers = {
        'accept': 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
        'accept-language': 'zh-CN,zh;q=0.9,en;q=0.8,zh-TW;q=0.7',
        'cache-control': 'no-cache',
        'pragma': 'no-cache',
        'priority': 'i',
        'referer': window.location.origin + '/imusic',
        'sec-fetch-dest': 'image',
        'sec-fetch-mode': 'no-cors',
        'sec-fetch-site': 'same-origin',
        'user-agent': navigator.userAgent
      };
      
      // 如果是API接口请求，添加token到URL参数
      if (coverUrl.includes('/api/music/') && coverUrl.includes('/cover')) {
        coverUrl = withCoverVersion(coverUrl, originalCoverUrl || coverUrl);
        const token = tokenService.getAccessToken();
        if (token) {
          // 如果URL中没有token参数，添加它
          if (!coverUrl.includes('token=')) {
            const separator = coverUrl.includes('?') ? '&' : '?';
            coverUrl = `${coverUrl}${separator}token=${encodeURIComponent(token)}`;
          }
        }
      }

      const isUnversionedApiCover = coverUrl.includes('/api/music/')
        && coverUrl.includes('/cover')
        && !coverUrl.includes('v=');
      
      const response = await fetch(coverUrl, {
        method: 'GET',
        headers,
        // 带文件名版本的地址可以缓存；没有版本的固定地址会把旧封面缓存 24 小时
        cache: isUnversionedApiCover ? 'no-store' : 'default'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // 验证是否为有效的图片
      if (!blob.type.startsWith('image/')) {
        throw new Error(`无效的图片类型: ${blob.type}`);
      }
      
      // 验证图片大小（防止损坏的图片）
      if (blob.size < 100) {
        throw new Error(`图片大小异常: ${blob.size} bytes`);
      }
      
      // 保存到IndexedDB缓存
      // 🔥 修复：使用原始 URL 来提取文件名，而不是转换后的 API URL
      const urlForCache = originalCoverUrl || coverUrl;
      await saveCoverToCache(songId, blob, urlForCache, false);
      
      // 创建Blob URL用于显示
      const blobUrl = URL.createObjectURL(blob);
      
      log.debug(`✅ [封面缓存] 网络加载封面成功 (尝试 ${attempt}): ${songId}, 大小: ${(blob.size / 1024).toFixed(1)}KB`);
      return {
        url: blobUrl,
        isDefault: false,
        cached: false
      };
      
    } catch (error) {
      lastError = error;
      log.warn(`⚠️ [封面缓存] 第 ${attempt} 次尝试失败 ${songId}:`, error.message);
      
      // 如果不是最后一次尝试，等待一段时间再重试
      if (attempt < retryCount) {
        const delay = Math.min(1000 * attempt, 3000); // 递增延迟，最大3秒
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  log.error(`❌ [封面缓存] 网络加载封面失败，已重试${retryCount}次 ${songId}:`, lastError);
  return null;
}

// ==================== 默认封面相关 ====================

/**
 * 获取默认封面
 * @param {string|number} songId - 歌曲ID
 * @returns {string} 默认封面URL
 */
function getDefaultCoverForSong(songId) {
  if (!songId) {
    log.warn('⚠️ [getDefaultCoverForSong] 歌曲ID无效:', songId);
    return DEFAULT_COVERS[0];
  }
  
  try {
    // 使用 getRandomDefaultCover 获取随机默认封面
    const defaultCover = getRandomDefaultCover(songId);
    return defaultCover;
  } catch (error) {
    log.error(`❌ [getDefaultCoverForSong] 获取默认封面失败: ${songId}`, error);
    return DEFAULT_COVERS[0];
  }
}

// ==================== 统一封面获取接口 ====================

/**
 * 🎯 统一封面获取接口 - getCoverUrl()
 * 功能：优先从缓存获取，如果没有缓存则自动加载并建立缓存
 * 支持：IndexedDB缓存、OneDrive文件ID转换、网络加载等
 * 特性：内置加载状态锁，防止同一封面被多个流程同时加载
 * @param {Object} song - 歌曲对象
 * @returns {Promise<string>} Blob URL（优先使用缓存，如果没有缓存则从网络加载）
 */
async function getCoverUrl(song) {
  if (!song || !song.id) {
    log.warn('⚠️ [getCoverUrl] 歌曲对象无效:', song);
    return null;
  }

  const songId = String(song.id);

  // 🔒 检查是否正在加载中（加载状态锁）
  if (coverLoadingLocks.has(songId)) {
    log.debug(`🔒 [getCoverUrl] 封面正在加载中，等待现有加载完成: ${song.title || songId}`);
    try {
      // 等待现有的加载完成
      return await coverLoadingLocks.get(songId);
    } catch (error) {
      // 如果现有加载失败，继续执行新的加载流程
      log.warn(`⚠️ [getCoverUrl] 等待现有加载失败，开始新的加载: ${song.title || songId}`, error);
      coverLoadingLocks.delete(songId);
    }
  }

  // 🔒 创建加载 Promise 并加入锁
  const loadingPromise = (async () => {
    try {
      // 初始化封面缓存数据库
      await initCoverCacheDB();

      // 1. 检查歌曲对象是否已有有效的 Blob URL
      // 🔥 注意：页面刷新后，blob URL 会失效，所以需要验证
      if (song.coverUrl && song.coverUrl.startsWith('blob:')) {
        // 🔥 修复：blob URL 不支持 HEAD 请求，改用 GET 请求验证
        // 但为了避免不必要的网络请求，先检查内存缓存中是否有对应的 blob URL
        if (coverCache.has(songId) && coverCache.get(songId) === song.coverUrl) {
          // 内存缓存中有对应的 blob URL，说明是有效的
          log.debug(`✅ [getCoverUrl] 使用歌曲对象已有的有效 Blob URL（内存缓存验证）: ${song.title || song.id}`);
          return song.coverUrl;
        }
        
        // 如果内存缓存中没有，尝试从 IndexedDB 验证
        // 但为了避免阻塞，先假设 blob URL 有效，如果后续加载失败再处理
        // 这样可以避免不必要的网络请求
        log.debug(`🔄 [getCoverUrl] Blob URL 未在内存缓存中，继续后续流程: ${song.title || song.id}`);
      }

      // 2. 获取原始封面URL（优先使用 _originalCoverUrl）
      // 🔥 重要：如果 song.coverUrl 是 blob URL，不要用它作为原始 URL
      // 因为 blob URL 在页面刷新后会失效，我们需要使用真正的服务器 URL
      let originalCoverUrl = song._originalCoverUrl;
      
      // 如果 _originalCoverUrl 不存在，且 coverUrl 不是 blob URL，才使用 coverUrl
      if (!originalCoverUrl && song.coverUrl && !song.coverUrl.startsWith('blob:')) {
        originalCoverUrl = song.coverUrl;
      }
      
      // 如果还是没有，尝试从其他字段获取
      if (!originalCoverUrl && song.cover && !song.cover.startsWith('blob:')) {
        originalCoverUrl = song.cover;
      }

      if (originalCoverUrl && typeof originalCoverUrl === 'string') {
        originalCoverUrl = normalizeOriginalCoverUrl(song, originalCoverUrl);
      }
      
      if (!originalCoverUrl || typeof originalCoverUrl !== 'string') {
        log.warn(`⚠️ [getCoverUrl] 歌曲没有有效的原始封面URL，尝试从IndexedDB缓存获取: ${song.title || song.id}`);
        
        // 🔥 尝试从 IndexedDB 缓存获取封面（即使没有原始 URL）
        // 这对于页面刷新后，歌曲对象只有失效的 blob URL 的情况很有用
        try {
          const cachedCover = await getCachedCover(song.id, null);
          if (cachedCover && !cachedCover.isDefault && cachedCover.url) {
            log.debug(`💾 [getCoverUrl] 从缓存获取封面（无原始URL）: ${song.title || song.id}`);
            // 🔥 关键修复：只有在值不同时才更新，避免不必要的响应式更新导致闪烁
            if (song.coverUrl !== cachedCover.url) {
              song.coverUrl = cachedCover.url;
            }
            coverCache.set(songId, song.coverUrl);
            song._coverCached = true;
            // 🔥 返回更新后的 song.coverUrl，确保返回值与更新值一致
            return song.coverUrl;
          }
        } catch (cacheError) {
          log.warn(`⚠️ [getCoverUrl] 从缓存获取封面失败: ${song.title || song.id}`, cacheError);
        }
        
        // 如果缓存也没有，返回默认封面
        const defaultCover = getDefaultCoverForSong(song.id);
        return defaultCover;
      }

      // 3. 检查是否是有效的服务器封面（排除默认封面）
      const hasValidServerCover = originalCoverUrl && 
                                  originalCoverUrl.trim() !== '' && 
                                  !originalCoverUrl.includes('default-music-cover-') &&
                                  !originalCoverUrl.includes('temp-cover') &&
                                  !originalCoverUrl.includes('default-cover') &&
                                  (originalCoverUrl.includes('/srv/uploads/') || 
                                   originalCoverUrl.includes('music-cover') ||
                                   originalCoverUrl.includes('/api/music/') ||
                                   isAliyunCover(originalCoverUrl) ||
                                   isOneDriveFileId(originalCoverUrl));

      if (!hasValidServerCover) {
        log.debug(`⏭️ [getCoverUrl] 歌曲没有有效服务器封面，使用默认封面: ${song.title || song.id}`);
        const defaultCover = getDefaultCoverForSong(song.id);
        return defaultCover;
      }

      // 4. 优先以原始URL/文件ID查询缓存，命中后直接返回，避免额外的网络调用
      let isOneDriveId = isOneDriveFileId(originalCoverUrl);
      const cacheIdentifier = originalCoverUrl;
      const cachedCover = await getCachedCover(song.id, cacheIdentifier);
      
      if (cachedCover && !cachedCover.isDefault && cachedCover.url) {
        log.debug(`💾 [getCoverUrl] 使用缓存封面: ${song.title || song.id}`);
        if (song.coverUrl !== cachedCover.url) {
          song.coverUrl = cachedCover.url;
        }
        coverCache.set(songId, song.coverUrl);
        if (coverFileVersion(cacheIdentifier)) {
          coverCache.set(`${songId}_ver`, coverFileVersion(cacheIdentifier));
        }
        song._coverCached = true;
        return song.coverUrl;
      }

      // 4.5 阿里云盘封面：走后端拿直链 → 浏览器 no-referrer 取图
      if (isAliyunCover(originalCoverUrl)) {
        log.debug(`🔍 [getCoverUrl] 检测到阿里云盘封面: ${song.title || song.id}`);
        const aliyunBlobUrl = await loadAliyunCover(song.id, originalCoverUrl);
        if (aliyunBlobUrl) {
          if (song.coverUrl !== aliyunBlobUrl) {
            song.coverUrl = aliyunBlobUrl;
          }
          coverCache.set(songId, song.coverUrl);
          song._coverCached = true;
          return song.coverUrl;
        }
        // 失败回退默认封面
        const defaultCover = USE_SVG_ICON;
        song.coverUrl = defaultCover;
        song._coverCached = true;
        song._coverFailed = true;
        return defaultCover;
      }

      // 5. 处理 OneDrive 文件ID转换 / 本地路径转换
      let finalCoverUrl = originalCoverUrl;
      
      if (isOneDriveId) {
        log.debug(`🔍 [getCoverUrl] 检测到 OneDrive 文件ID: ${song.title || song.id}`);
        
        try {
          // 优先使用 Microsoft Graph Thumbnails 接口获取大图（large）
          // https://graph.microsoft.com/v1.0/me/drive/items/{item-id}/thumbnails/0/large
          // 需要 Bearer accessToken，可通过现有的 getUploadAuth 复用获取
          let thumbnailUrl = null;
          try {
            const auth = await oneDriveAPI.getUploadAuth(false);
            if (auth && auth.accessToken) {
              const graphThumbEndpoint = `https://graph.microsoft.com/v1.0/me/drive/items/${encodeURIComponent(originalCoverUrl)}/thumbnails/0/large`;
              const thumbResp = await fetch(graphThumbEndpoint, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${auth.accessToken}`,
                  'Accept': 'application/json'
                }
              });
              if (thumbResp.ok) {
                const thumbJson = await thumbResp.json();
                // Graph 返回对象可能包含 width/height/url
                if (thumbJson && thumbJson.url) {
                  thumbnailUrl = thumbJson.url;
                }
              } else {
                log.warn(`⚠️ [getCoverUrl] 获取 OneDrive 缩略图失败: HTTP ${thumbResp.status}`);
              }
            } else {
              log.warn('⚠️ [getCoverUrl] 无法获取 OneDrive 访问令牌，用于缩略图请求，回退到下载URL');
            }
          } catch (thumbErr) {
            log.warn('⚠️ [getCoverUrl] 获取 OneDrive 缩略图异常，回退到下载URL:', thumbErr);
          }

          if (thumbnailUrl) {
            // 使用大图作为列表默认封面
            finalCoverUrl = thumbnailUrl;
            log.debug(`✅ [getCoverUrl] 使用 OneDrive 大图缩略图(large): ${song.title || song.id}`);
          } else {
            // 回退：使用原有的下载URL获取原图
            const urlMap = await oneDriveAPI.batchGetFileDownloadUrls([originalCoverUrl]);
            if (urlMap[originalCoverUrl]) {
              finalCoverUrl = urlMap[originalCoverUrl];
              log.debug(`✅ [getCoverUrl] OneDrive 文件ID转换成功(回退原图): ${song.title || song.id}`);
            } else {
              log.warn(`⚠️ [getCoverUrl] OneDrive 文件ID转换失败: ${song.title || song.id}`);
              const defaultCover = getDefaultCoverForSong(song.id);
              return defaultCover;
            }
          }
        } catch (error) {
          log.error(`❌ [getCoverUrl] OneDrive 文件ID转换异常: ${song.title || song.id}`, error);
          const defaultCover = getDefaultCoverForSong(song.id);
          return defaultCover;
        }
      } else {
        // 转换文件系统路径为 API 路径
        finalCoverUrl = convertToHttpCoverUrl(originalCoverUrl, song.id);
        if (!finalCoverUrl) {
          log.warn(`⚠️ [getCoverUrl] 封面URL转换失败: ${song.title || song.id}`);
          const defaultCover = getDefaultCoverForSong(song.id);
          return defaultCover;
        }
      }

      // 6. 没有缓存，从网络加载封面
      log.debug(`🌐 [getCoverUrl] 从网络加载封面: ${song.title || song.id}`);
      // 🔥 修复：传入原始 URL 用于文件名提取
      const networkCover = await loadCoverFromNetwork(finalCoverUrl, song.id, 3, originalCoverUrl);
      
      if (networkCover && !networkCover.isDefault && networkCover.url) {
        // 网络加载成功
        log.debug(`✅ [getCoverUrl] 网络加载封面成功: ${song.title || song.id}`);
        
        // 🔥 关键修复：只有在值不同时才更新，避免不必要的响应式更新导致闪烁
        if (song.coverUrl !== networkCover.url) {
          song.coverUrl = networkCover.url;
        }
        coverCache.set(songId, song.coverUrl);
        if (coverFileVersion(originalCoverUrl)) {
          coverCache.set(`${songId}_ver`, coverFileVersion(originalCoverUrl));
        }
        song._coverCached = true;
        
        // 对于 OneDrive 文件ID，需要重新保存缓存，使用文件ID作为标识
        if (isOneDriveId) {
          try {
            // 从 blob URL 获取 blob 对象
            const blobResponse = await fetch(networkCover.url);
            const blob = await blobResponse.blob();
            
            // 使用文件ID作为URL标识重新保存缓存（IndexedDB）
            await saveCoverToCache(song.id, blob, originalCoverUrl, false);
            
            log.debug(`💾 [getCoverUrl] OneDrive 封面已缓存（使用文件ID标识）: ${song.title || song.id}`);
          } catch (cacheError) {
            log.warn(`⚠️ [getCoverUrl] 重新保存 OneDrive 缓存失败（不影响使用）: ${song.title || song.id}`, cacheError);
          }
        }
        
        // 🔥 移除 getSmartCoverUrl 调用，避免触发额外的逻辑导致闪烁
        // getCoverUrl() 已经更新了 song.coverUrl，不需要再次调用
        
        // 🔥 返回更新后的 song.coverUrl，确保返回值与更新值一致
        return song.coverUrl;
      } else {
        // 网络加载失败，使用默认封面（SVG图标）
        log.warn(`⚠️ [getCoverUrl] 网络加载封面失败，使用默认封面: ${song.title || song.id}`);
        const defaultCover = USE_SVG_ICON; // 🔥 修复：直接使用 USE_SVG_ICON 而不是 getDefaultCoverForSong
        
        // 🔥 关键修复：只有在值不同时才更新，避免不必要的响应式更新导致闪烁
        if (song.coverUrl !== defaultCover) {
          song.coverUrl = defaultCover;
        }
        song._coverCached = true;
        song._coverFailed = true;
        
        return defaultCover;
      }
    } catch (error) {
      log.error(`❌ [getCoverUrl] 获取封面时出错: ${song.title || song.id}`, error);
      
      // 异常时返回默认封面（SVG图标）
      const defaultCover = USE_SVG_ICON; // 🔥 修复：直接使用 USE_SVG_ICON
      // 🔥 关键修复：只有在值不同时才更新，避免不必要的响应式更新导致闪烁
      if (song.coverUrl !== defaultCover) {
        song.coverUrl = defaultCover;
      }
      song._coverCached = true;
      song._coverFailed = true;
      return defaultCover;
    } finally {
      // 🔓 加载完成，释放锁
      coverLoadingLocks.delete(songId);
    }
  })();

  // 将加载 Promise 加入锁
  coverLoadingLocks.set(songId, loadingPromise);

  // 返回加载 Promise
  return loadingPromise;
}

/**
 * 封面服务配置
 */
// 🎯 刷新某首歌的封面缓存为“原图”
// 传入原图直链（如 OneDrive 下载直链），内部完成下载、写入 IndexedDB、并更新内存与传入的 song.coverUrl 为 blob URL
export async function refreshCoverCacheWithOriginal(song, originalDirectUrl) {
  if (!song || !song.id || !originalDirectUrl) return false;
  try {
    const resp = await fetch(originalDirectUrl, { method: 'GET' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    if (!blob || !blob.type.startsWith('image/')) throw new Error('Invalid image blob');

    // 保存到 IndexedDB（使用原始标识：如果有 OneDrive 文件ID，传入它；否则传入 originalDirectUrl）
    const identifier = song._originalCoverUrl || song.coverUrl || originalDirectUrl;
    await saveCoverToCache(String(song.id), blob, identifier, false);

    // 更新内存缓存与当前对象的封面为 blob URL
    const blobUrl = URL.createObjectURL(blob);
    coverCache.set(String(song.id), blobUrl);
    song.coverUrl = blobUrl;
    song._coverCached = true;
    return true;
  } catch (e) {
    log.warn('⚠️ [CoverService] 刷新原图封面缓存失败:', e);
    return false;
  }
}

const mediaSessionArtworkCache = new Map();

function pickHttpsCoverUrl(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.startsWith('https://')) return trimmed;
  return null;
}

function extractAliyunCoverFileId(song) {
  if (!song) return null;

  const candidates = [
    song.coverDirectUrl,
    song.coverUrl,
    song.cover,
    song._originalCoverUrl
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.startsWith(ALIYUN_COVER_PREFIX)) {
      return candidate.slice(ALIYUN_COVER_PREFIX.length);
    }
  }

  const raw = song._originalCoverUrl || song.coverUrl || song.cover;
  if (isAliyunStorageSong(song) && raw && isOneDriveFileId(raw)) {
    return raw;
  }

  return null;
}

/**
 * 解析锁屏/通知栏可用的封面 HTTPS 直链。
 * 阿里云盘优先使用后端返回的缩略图 CDN 链接，不额外消耗下载次数。
 */
async function resolveMediaSessionArtworkUrl(song) {
  if (!song) return null;

  const directHttps = pickHttpsCoverUrl(song.coverDirectUrl);
  if (directHttps) return directHttps;

  for (const key of ['coverUrl', 'cover', '_originalCoverUrl']) {
    const httpsUrl = pickHttpsCoverUrl(song[key]);
    if (httpsUrl) return httpsUrl;
  }

  const aliyunFileId = extractAliyunCoverFileId(song);
  if (aliyunFileId) {
    const cacheKey = `aliyun:${aliyunFileId}`;
    if (mediaSessionArtworkCache.has(cacheKey)) {
      return mediaSessionArtworkCache.get(cacheKey);
    }

    const thumbnailUrl = await fetchAliyunCoverDirectUrl(aliyunFileId, false);
    if (pickHttpsCoverUrl(thumbnailUrl)) {
      mediaSessionArtworkCache.set(cacheKey, thumbnailUrl);
      return thumbnailUrl;
    }

    const downloadUrl = await fetchAliyunCoverDirectUrl(aliyunFileId, false, null, 'download');
    if (pickHttpsCoverUrl(downloadUrl)) {
      mediaSessionArtworkCache.set(cacheKey, downloadUrl);
      return downloadUrl;
    }
  }

  return null;
}

export const CoverService = {
  getSongCoverUrl,
  getSmartCoverUrl,
  cacheOnlineCover,
  getCachedOnlineCover,
  preloadCovers,
  clearCoverCache,
  isDefaultCover,
  getDefaultCoverIdentifier,
  hasBackendCover,
  initializeCoverService,
  resolveMediaSessionArtworkUrl,
  // 🎯 新增：统一的封面获取接口
  getCoverUrl,
  // 🎯 新增：暴露刷新原图缓存方法，供播放页调用
  refreshCoverCacheWithOriginal
};

export default CoverService;
